import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Refresh, Trash } from "iconoir-react";
import {
    fetchAdminOverview,
    fetchAdminFamilies,
    fetchAdminFeedback,
    fetchAdminStickiness,
    fetchAdminWebhookStatus,
    fetchAdminUsers,
    updateUserPlan,
    updateFeedbackStatus,
    deleteHouseholdAdmin,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "families", label: "Families" },
    { id: "billing", label: "Billing" },
    { id: "feedback", label: "Feedback" },
];

const PLAN_COLORS = {
    free: "bg-white text-[#0D2A1B] border-[#0D2A1B]/20",
    plus: "bg-[#BCE3B6] text-[#0D2A1B] border-[#2F6B43]/40",
    premium: "bg-[#D4C5E8] text-[#0D2A1B] border-[#7A5BA8]/40",
};

const STATUS_PILL_COLORS = {
    trial: "bg-[#F3D469]/40 text-[#7a5c1a]",
    active: "bg-[#BCE3B6]/60 text-[#1f4d2f]",
    manual_plus: "bg-[#B8DBEF]/60 text-[#1a4a6b]",
    manual_premium: "bg-[#D4C5E8]/70 text-[#4a2a7a]",
    past_due: "bg-[#F4B7B2]/60 text-[#7a2a2a]",
    canceled: "bg-[#E5E5E5] text-[#5b6d63]",
};

const STATUS_COLORS = {
    healthy: "bg-[#BCE3B6] text-[#0D2A1B]",
    low: "bg-[#F3D469] text-[#0D2A1B]",
    inactive: "bg-[#F4B7B2] text-[#0D2A1B]",
};

const FEEDBACK_STATUS_COLORS = {
    new: "bg-[#F3D469]",
    reviewed: "bg-[#BCE3B6]",
    planned: "bg-[#B8DBEF]",
    fixed: "bg-[#D4C5E8]",
    ignored: "bg-[#E5E5E5]",
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}

function daysAgo(iso) {
    if (!iso) return "—";
    const days = (Date.now() - new Date(iso).getTime()) / 86400000;
    if (days < 1) return "today";
    if (days < 2) return "yesterday";
    return `${Math.floor(days)}d ago`;
}

export default function AdminPage() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [tab, setTab] = useState("overview");
    const [overview, setOverview] = useState(null);
    const [families, setFamilies] = useState([]);
    const [feedback, setFeedback] = useState([]);
    const [stickiness, setStickiness] = useState(null);
    const [webhookStatus, setWebhookStatus] = useState(null);
    const [users, setUsers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [filters, setFilters] = useState({ status: "", has_bug: false, has_feature: false });

    const refresh = useCallback(async () => {
        setBusy(true);
        try {
            const [o, f, fb, st, wh, us] = await Promise.all([
                fetchAdminOverview(),
                fetchAdminFamilies(),
                fetchAdminFeedback({
                    ...(filters.status ? { status: filters.status } : {}),
                    ...(filters.has_bug ? { has_bug: true } : {}),
                    ...(filters.has_feature ? { has_feature: true } : {}),
                }),
                fetchAdminStickiness(8).catch(() => null),
                fetchAdminWebhookStatus().catch(() => null),
                fetchAdminUsers().catch(() => ({ users: [] })),
            ]);
            setOverview(o);
            setFamilies(f.families || []);
            setFeedback(fb.feedback || []);
            setStickiness(st);
            setWebhookStatus(wh);
            setUsers(us?.users || []);
        } catch (e) {
            toast.error("Failed to load admin data");
        } finally {
            setBusy(false);
        }
    }, [filters]);

    useEffect(() => {
        if (!loading && (!user || !user.is_admin)) {
            nav("/app", { replace: true });
        }
    }, [user, loading, nav]);

    useEffect(() => {
        if (user?.is_admin) refresh();
    }, [user, refresh]);

    const setFbStatus = async (id, status) => {
        try {
            await updateFeedbackStatus(id, status);
            setFeedback((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
            toast.success(`Marked ${status}`);
        } catch {
            toast.error("Failed to update");
        }
    };

    const setUserPlan = async (user_id, updates) => {
        const prev = users.find((u) => u.user_id === user_id);
        // optimistic
        setUsers((list) => list.map((u) => (u.user_id === user_id ? { ...u, ...updates } : u)));
        try {
            await updateUserPlan(user_id, updates);
            toast.success("Plan updated");
        } catch (e) {
            // revert
            if (prev) setUsers((list) => list.map((u) => (u.user_id === user_id ? prev : u)));
            toast.error(e?.response?.data?.detail || "Failed to update plan");
        }
    };

    const deleteFamily = async (household_id, name) => {
        if (!window.confirm(`Delete household "${name}" and ALL its data?\n\nThis is permanent. Members, meals, shopping, chores, chat, and activity will be wiped.`)) return;
        try {
            const res = await deleteHouseholdAdmin(household_id);
            setFamilies((prev) => prev.filter((f) => f.household_id !== household_id));
            toast.success(`Deleted "${res.name}"`);
            // Refresh overview KPIs
            fetchAdminOverview().then(setOverview).catch(() => {});
        } catch {
            toast.error("Failed to delete");
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">Loading…</div>;
    }
    if (!user?.is_admin) return null;

    return (
        <div className="min-h-screen flex flex-col" data-testid="admin-page">
            <header className="sticky top-0 z-30 bg-[#FBF6EB] border-b-2 border-[#0D2A1B]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link to="/app" className="brut-border rounded-lg bg-white w-10 h-10 flex items-center justify-center hover:bg-[#EFEAD8]" data-testid="admin-back-btn">
                            <ArrowLeft className="w-4 h-4" />
                        </Link>
                        <div className="min-w-0">
                            <div className="font-heading text-xl font-black leading-tight truncate">Admin · Milo pilot</div>
                            <div className="text-xs font-bold text-[#255636]">Internal — {user.email}</div>
                        </div>
                    </div>
                    <button onClick={refresh} disabled={busy} className="brut-btn brut-btn-secondary text-sm" data-testid="admin-refresh-btn">
                        <Refresh className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>

                <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-2 flex items-center gap-2 overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`px-4 py-1.5 text-sm font-bold brut-border rounded-full whitespace-nowrap transition-colors ${tab === t.id ? "bg-[#0D2A1B] text-[#FBF6EB]" : "bg-white hover:bg-[#EFEAD8]"
                                }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-6">
                {tab === "overview" && <Overview data={overview} families={families} stickiness={stickiness} webhookStatus={webhookStatus} />}
                {tab === "families" && <FamiliesTab families={families} onDelete={deleteFamily} />}
                {tab === "billing" && <BillingTab users={users} onUpdate={setUserPlan} />}
                {tab === "feedback" && (
                    <FeedbackTab feedback={feedback} filters={filters} setFilters={setFilters} setFbStatus={setFbStatus} />
                )}
            </main>
        </div>
    );
}

function KpiCard({ label, value, hint }) {
    return (
        <div className="brut-card p-4" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="text-xs uppercase tracking-widest font-bold text-[#255636]">{label}</div>
            <div className="font-heading text-3xl font-black mt-1">{value ?? "—"}</div>
            {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
        </div>
    );
}

function Overview({ data, families, stickiness, webhookStatus }) {
    const atRisk = (families || []).filter((f) => f.status !== "healthy").length;
    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                <KpiCard label="Total families" value={data?.total_households} />
                <KpiCard label="Active today" value={data?.dau_families} />
                <KpiCard label="WAU (7d)" value={data?.wau_families} />
                <KpiCard label="MAU (30d)" value={data?.mau_families} />
                <KpiCard label="WAU/MAU" value={data?.wau_mau_ratio} hint="stickiness" />
                <KpiCard label="Actions/fam/day" value={data?.actions_per_family_per_day_7d} hint="last 7d" />
                <KpiCard label="At-risk families" value={atRisk} hint="low or inactive" />
                <KpiCard label="Total users" value={data?.total_users} />
            </div>

            <ActivationCard stickiness={stickiness} />
            <HabitCard stickiness={stickiness} />
            <RetentionCard stickiness={stickiness} webhookStatus={webhookStatus} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="brut-card p-5">
                    <div className="font-heading text-lg font-black mb-3">Feedback snapshot</div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <Stat label="Total" value={data?.total_feedback} />
                        <Stat label="New (unreviewed)" value={data?.new_feedback} />
                        <Stat label="Bug reports" value={data?.total_bugs} />
                        <Stat label="Feature requests" value={data?.total_features} />
                        <Stat label="Avg rating" value={data?.avg_rating ?? "—"} />
                    </div>
                </div>
                <div className="brut-card p-5">
                    <div className="font-heading text-lg font-black mb-3">Family status</div>
                    <StatusBreakdown families={families} />
                </div>
            </div>
        </motion.div>
    );
}

// ===== Benchmark thresholds (industry-tuned for household productivity apps) =====
// Returns { tone: 'worry'|'ok'|'strong'|'excellent', label: string }
function rankPct(value, [worry, ok, strong]) {
    const v = value ?? 0;
    if (v < worry) return { tone: "worry", label: "Worry" };
    if (v < ok) return { tone: "ok", label: "OK" };
    if (v < strong) return { tone: "strong", label: "Strong" };
    return { tone: "excellent", label: "Excellent" };
}
const BENCH = {
    any_24h:        [40, 55, 70],   // any action in 24h
    activated_7d:   [20, 35, 50],   // chore + shopping + meal in 7 days
    two_plus:       [25, 40, 55],   // 2+ members active in 7 days
    day7:           [15, 25, 40],
    day14:          [10, 20, 30],
    day30:          [10, 20, 35],
    open_rate:      [25, 40, 55],
    click_rate:     [2,  5,  10],
    wau_mau:        [20, 35, 50],   // expressed as % (so 0.35 → 35)
};

function BenchPill({ rank, "data-testid": testId }) {
    const styles = {
        worry:     "bg-[#F4B7B2] text-[#7a2a2a] border-[#e89992]",
        ok:        "bg-[#F3D469] text-[#7a5c1a] border-[#e8c878]",
        strong:    "bg-[#BCE3B6] text-[#1f4d2f] border-[#9bd093]",
        excellent: "bg-[#2F6B43] text-[#FBF6EB] border-[#1f4d2f]",
    };
    const dot = {
        worry:     "🔴",
        ok:        "🟡",
        strong:    "🟢",
        excellent: "🌟",
    };
    return (
        <span data-testid={testId} className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${styles[rank.tone]}`}>
            <span>{dot[rank.tone]}</span>{rank.label}
        </span>
    );
}

function BenchKpi({ label, value, suffix = "%", bench, hint, "data-testid": testId }) {
    const numeric = typeof value === "number" ? value : null;
    const rank = numeric !== null && bench ? rankPct(numeric, bench) : null;
    const display = numeric === null || value === undefined ? "—" : (suffix ? `${value}${suffix}` : value);
    return (
        <div data-testid={testId} className="bg-white/70 rounded-xl p-3 border border-[#0d2a1b]/8">
            <div className="flex items-baseline justify-between gap-2 mb-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#255636]">{label}</div>
                {rank && <BenchPill rank={rank} />}
            </div>
            <div className="font-heading text-2xl font-black tabular-nums">{display}</div>
            {hint && <div className="text-[11px] text-[#5b6d63] mt-0.5">{hint}</div>}
        </div>
    );
}

function CardHeader({ icon, title, subtitle }) {
    return (
        <div className="flex items-baseline justify-between mb-1">
            <div className="font-heading text-lg font-black flex items-center gap-2">
                <span aria-hidden>{icon}</span>{title}
            </div>
            <div className="text-xs text-zinc-500">{subtitle}</div>
        </div>
    );
}

// ============ ACTIVATION ============
function ActivationCard({ stickiness }) {
    const a = stickiness?.activation;
    const cohorts = a?.cohorts || [];
    return (
        <div className="brut-card p-5" data-testid="activation-card">
            <CardHeader icon="🌱" title="Activation" subtitle="Day 0–7 · cohort by signup week" />
            <div className="text-xs text-[#255636] mb-4">
                Did this family get to value in their first 7 days?
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <BenchKpi
                    label="Signups (8w)"
                    value={a?.total_signups ?? 0}
                    suffix=""
                    data-testid="kpi-signups"
                />
                <BenchKpi
                    label="Any 24h action"
                    value={a?.any_action_pct ?? 0}
                    bench={BENCH.any_24h}
                    data-testid="kpi-any-24h"
                />
                <BenchKpi
                    label="Activated in 7d"
                    value={a?.activated_7d_pct ?? 0}
                    bench={BENCH.activated_7d}
                    hint="chore + shopping + meal"
                    data-testid="kpi-activated"
                />
                <BenchKpi
                    label="2+ members in 7d"
                    value={a?.two_plus_members_pct ?? 0}
                    bench={BENCH.two_plus}
                    hint="multi-player households"
                    data-testid="kpi-two-plus"
                />
            </div>
            {a?.median_time_to_first_action_min !== null && a?.median_time_to_first_action_min !== undefined && (
                <div className="text-xs text-[#5b6d63] mb-4">
                    Median time from signup to first action: <b>{a.median_time_to_first_action_min} min</b>
                </div>
            )}

            {cohorts.length === 0 ? (
                <div className="text-sm text-zinc-500 py-3">No signups in the last 8 weeks.</div>
            ) : (
                <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full text-sm" data-testid="activation-cohort-table">
                        <thead>
                            <tr className="text-[11px] uppercase tracking-widest text-[#255636] text-left">
                                <th className="py-2 pr-3 font-bold">Cohort</th>
                                <th className="py-2 px-2 font-bold text-right">Signups</th>
                                <th className="py-2 px-2 font-bold text-right" title="Any of the 3 milestones in 24h">Any 24h</th>
                                <th className="py-2 px-2 font-bold text-right">Chore</th>
                                <th className="py-2 px-2 font-bold text-right">Shopping</th>
                                <th className="py-2 px-2 font-bold text-right">Meal</th>
                                <th className="py-2 px-2 font-bold text-right">Activated 7d</th>
                                <th className="py-2 pl-2 font-bold text-right">2+ mem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cohorts.map((c) => (
                                <tr key={c.week} className="border-t border-[#0d2a1b]/8" data-testid={`activation-row-${c.week}`}>
                                    <td className="py-2 pr-3 font-bold">{c.week}</td>
                                    <td className="py-2 px-2 text-right tabular-nums">{c.signups}</td>
                                    <td className="py-2 px-2 text-right tabular-nums"><PctPill value={c.any_action_pct} bold /></td>
                                    <td className="py-2 px-2 text-right tabular-nums"><PctPill value={c.first_chore_pct} /></td>
                                    <td className="py-2 px-2 text-right tabular-nums"><PctPill value={c.first_shopping_pct} /></td>
                                    <td className="py-2 px-2 text-right tabular-nums"><PctPill value={c.first_meal_pct} /></td>
                                    <td className="py-2 px-2 text-right tabular-nums"><PctPill value={c.activated_7d_pct} /></td>
                                    <td className="py-2 pl-2 text-right tabular-nums"><PctPill value={c.two_plus_members_pct} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ============ HABIT ============
function HabitCard({ stickiness }) {
    const h = stickiness?.habit;
    const wauMauPct = h?.wau_mau_ratio !== undefined ? Math.round(h.wau_mau_ratio * 100) : 0;
    const mix = h?.input_mix || {};
    return (
        <div className="brut-card p-5" data-testid="habit-card">
            <CardHeader icon="🔁" title="Habit" subtitle="Day 5–14 · are they coming back?" />
            <div className="text-xs text-[#255636] mb-4">
                Repeat usage signals real product-market fit — not a one-time visit.
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <BenchKpi
                    label="Day 7 retention"
                    value={h?.day7_retention?.pct ?? 0}
                    bench={BENCH.day7}
                    hint={`${h?.day7_retention?.retained ?? 0}/${h?.day7_retention?.eligible ?? 0} users`}
                    data-testid="kpi-d7"
                />
                <BenchKpi
                    label="Day 14 retention"
                    value={h?.day14_retention?.pct ?? 0}
                    bench={BENCH.day14}
                    hint={`${h?.day14_retention?.retained ?? 0}/${h?.day14_retention?.eligible ?? 0} users`}
                    data-testid="kpi-d14"
                />
                <BenchKpi
                    label="WAU/MAU"
                    value={wauMauPct}
                    bench={BENCH.wau_mau}
                    hint={`${h?.wau_families ?? 0}/${h?.mau_families ?? 0} families`}
                    data-testid="kpi-waumau"
                />
                <BenchKpi
                    label="Actions (14d)"
                    value={mix.total_actions_14d ?? 0}
                    suffix=""
                    data-testid="kpi-actions-14d"
                />
            </div>

            {/* Input mix bar */}
            <div className="mt-4">
                <div className="flex items-baseline justify-between mb-1.5">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-[#255636]">Input mix · last 14 days</div>
                    <div className="text-[11px] text-[#5b6d63]">{mix.total_actions_14d ?? 0} total actions</div>
                </div>
                {(mix.total_actions_14d ?? 0) === 0 ? (
                    <div className="text-sm text-zinc-500 py-2">No actions in the last 14 days.</div>
                ) : (
                    <div className="space-y-2">
                        <div className="flex h-3 rounded-full overflow-hidden border border-[#0d2a1b]/10" data-testid="input-mix-bar">
                            <div className="bg-[#2F6B43]" style={{ width: `${mix.voice_pct ?? 0}%` }} title={`Voice ${mix.voice_pct}%`} />
                            <div className="bg-[#F3D469]" style={{ width: `${mix.chat_pct ?? 0}%` }} title={`Chat ${mix.chat_pct}%`} />
                            <div className="bg-[#F4B7B2]" style={{ width: `${mix.tap_pct ?? 0}%` }} title={`Tap ${mix.tap_pct}%`} />
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs">
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#2F6B43]" /> Voice <b className="tabular-nums">{mix.voice_pct ?? 0}%</b></span>
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#F3D469]" /> Chat <b className="tabular-nums">{mix.chat_pct ?? 0}%</b></span>
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#F4B7B2]" /> Tap <b className="tabular-nums">{mix.tap_pct ?? 0}%</b></span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ============ RETENTION ============
function RetentionCard({ stickiness, webhookStatus }) {
    const r = stickiness?.retention;
    const tpls = r?.per_template || [];
    const churn = r?.churn_warning || [];
    const opensTracked = (r?.open_rate_overall ?? 0) > 0 || (r?.click_rate_overall ?? 0) > 0;
    const evCount = webhookStatus?.events_received?.total_events ?? 0;
    const secretConfigured = !!webhookStatus?.secret_configured;
    const lastEvent = webhookStatus?.last_event_at;
    const webhookHealthy = secretConfigured && evCount > 0;
    return (
        <div className="brut-card p-5" data-testid="retention-card">
            <CardHeader icon="💚" title="Retention" subtitle="Day 14–30 · is it becoming a habit?" />
            <div className="text-xs text-[#255636] mb-4">
                Long-term value — and who's about to slip away.
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <BenchKpi
                    label="Day 30 retention"
                    value={r?.day30_retention?.pct ?? 0}
                    bench={BENCH.day30}
                    hint={`${r?.day30_retention?.retained ?? 0}/${r?.day30_retention?.eligible ?? 0} users`}
                    data-testid="kpi-d30"
                />
                <BenchKpi
                    label="Email open rate"
                    value={opensTracked ? (r?.open_rate_overall ?? 0) : null}
                    bench={BENCH.open_rate}
                    hint={opensTracked ? "across all templates" : "webhook not configured"}
                    data-testid="kpi-open"
                />
                <BenchKpi
                    label="Email click rate"
                    value={opensTracked ? (r?.click_rate_overall ?? 0) : null}
                    bench={BENCH.click_rate}
                    hint={opensTracked ? "across all templates" : "webhook not configured"}
                    data-testid="kpi-click"
                />
                <BenchKpi
                    label="Churn warning"
                    value={r?.churn_warning_total ?? 0}
                    suffix=""
                    hint="silent ≥ 7 days"
                    data-testid="kpi-churn"
                />
            </div>

            {!webhookHealthy && (
                <div className="mb-3 text-[11px] text-[#7a5c1a] bg-[#fbeecb] border border-[#e8c878] rounded-lg px-3 py-2" data-testid="retention-webhook-note">
                    {!secretConfigured && (
                        <>
                            <b>Resend webhook not configured.</b> Add it in your Resend dashboard:
                            <ol className="ml-4 mt-1 list-decimal space-y-0.5">
                                <li>URL: <code>{`{your-domain}/api/webhooks/resend`}</code></li>
                                <li>Events: <code>email.opened</code>, <code>email.clicked</code>, <code>email.delivered</code>, <code>email.bounced</code></li>
                                <li>Copy the signing secret → set <code>RESEND_WEBHOOK_SECRET</code> in backend .env → redeploy</li>
                            </ol>
                        </>
                    )}
                    {secretConfigured && evCount === 0 && (
                        <>
                            <b>Webhook secret configured ✅, but no events received yet.</b> Send a test email or wait for the next lifecycle send. (Resend sometimes takes a few minutes to deliver the first events.)
                        </>
                    )}
                </div>
            )}
            {webhookHealthy && !opensTracked && (
                <div className="mb-3 text-[11px] text-[#1f4d2f] bg-[#dff0d8] border border-[#a8d8ab] rounded-lg px-3 py-2">
                    <b>Webhook receiving events ✅</b> — {evCount} event{evCount === 1 ? "" : "s"} so far. Per-template open/click rates will appear once you start sending lifecycle emails to active users.
                    {lastEvent && <span className="block mt-0.5 opacity-75">Last event: {new Date(lastEvent).toLocaleString()}</span>}
                </div>
            )}

            {/* Per-template email table */}
            <div className="overflow-x-auto -mx-1 px-1 mb-4">
                <table className="w-full text-sm" data-testid="retention-templates-table">
                    <thead>
                        <tr className="text-[11px] uppercase tracking-widest text-[#255636] text-left">
                            <th className="py-2 pr-3 font-bold">Template</th>
                            <th className="py-2 px-2 font-bold text-right">Sent</th>
                            <th className="py-2 px-2 font-bold text-right">Opened</th>
                            <th className="py-2 px-2 font-bold text-right">Clicked</th>
                            <th className="py-2 px-2 font-bold text-right">Open %</th>
                            <th className="py-2 pl-2 font-bold text-right">Click %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tpls.map((t) => (
                            <tr key={t.type} className="border-t border-[#0d2a1b]/8" data-testid={`tpl-row-${t.type}`}>
                                <td className="py-2 pr-3 font-bold capitalize">{t.type}</td>
                                <td className="py-2 px-2 text-right tabular-nums">{t.sent}</td>
                                <td className="py-2 px-2 text-right tabular-nums">{t.opened}</td>
                                <td className="py-2 px-2 text-right tabular-nums">{t.clicked}</td>
                                <td className="py-2 px-2 text-right tabular-nums">{t.sent ? <PctPill value={t.open_rate} /> : <span className="text-zinc-400">—</span>}</td>
                                <td className="py-2 pl-2 text-right tabular-nums">{t.sent ? <PctPill value={t.click_rate} /> : <span className="text-zinc-400">—</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Churn warning list */}
            {churn.length > 0 && (
                <div className="border-t border-[#0d2a1b]/10 pt-4">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-[#a04d3a] mb-2">⚠️ Churn warning · silent ≥ 7 days</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="churn-list">
                        {churn.slice(0, 8).map((h) => (
                            <div key={h.household_id} className="flex items-center justify-between bg-[#fbeecb]/60 border border-[#e8c878]/60 rounded-lg px-3 py-2">
                                <div className="font-semibold text-sm truncate">{h.name}</div>
                                <div className="text-xs text-[#7a5c1a] tabular-nums whitespace-nowrap ml-2">{h.days_silent}d silent</div>
                            </div>
                        ))}
                    </div>
                    {churn.length > 8 && (
                        <div className="text-[11px] text-[#5b6d63] mt-2">+ {churn.length - 8} more</div>
                    )}
                </div>
            )}
        </div>
    );
}

function PctPill({ value, bold }) {
    const v = value ?? 0;
    const tone =
        v >= 60 ? "bg-[#BCE3B6] text-[#0d2a1b]" :
        v >= 30 ? "bg-[#F3D469] text-[#0d2a1b]" :
        "bg-[#F4B7B2] text-[#0d2a1b]";
    return (
        <span className={`inline-block min-w-[44px] text-center px-2 py-0.5 rounded-full text-xs ${bold ? "font-black" : "font-semibold"} ${tone}`}>
            {v}%
        </span>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#255636]">{label}</div>
            <div className="font-heading text-2xl font-black">{value ?? "—"}</div>
        </div>
    );
}

function StatusBreakdown({ families }) {
    const counts = { healthy: 0, low: 0, inactive: 0 };
    (families || []).forEach((f) => {
        counts[f.status] = (counts[f.status] || 0) + 1;
    });
    const total = counts.healthy + counts.low + counts.inactive || 1;
    return (
        <div className="space-y-2">
            {(["healthy", "low", "inactive"]).map((s) => {
                const n = counts[s] || 0;
                const pct = Math.round((n / total) * 100);
                return (
                    <div key={s} className="flex items-center gap-3">
                        <div className={`px-2.5 py-0.5 rounded-full text-xs font-bold brut-border ${STATUS_COLORS[s]}`}>
                            {s}
                        </div>
                        <div className="flex-1 h-3 brut-border bg-[#FBF6EB] overflow-hidden">
                            <div className={`h-full ${s === "healthy" ? "bg-[#2F6B43]" : s === "low" ? "bg-[#F3D469]" : "bg-[#E86A5C]"}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-sm font-bold w-10 text-right">{n}</div>
                    </div>
                );
            })}
        </div>
    );
}

function FamiliesTab({ families, onDelete }) {
    if (!families?.length) {
        return <div className="brut-card p-6 text-center text-sm text-zinc-500">No families yet.</div>;
    }
    return (
        <div className="brut-card overflow-x-auto" data-testid="families-table">
            <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-widest font-bold bg-[#EFEAD8] border-b-2 border-[#0D2A1B]">
                    <tr>
                        <th className="p-3">Family</th>
                        <th className="p-3">Members</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Last active</th>
                        <th className="p-3">Days 7d</th>
                        <th className="p-3">Actions 7d</th>
                        <th className="p-3">Actions 30d</th>
                        <th className="p-3">Most used</th>
                        <th className="p-3">Chores ✓ 7d</th>
                        <th className="p-3">Rating</th>
                        <th className="p-3"></th>
                    </tr>
                </thead>
                <tbody>
                    {families.map((f) => (
                        <tr key={f.household_id} className="border-b border-[#0D2A1B]/10 hover:bg-[#FBF6EB]/50">
                            <td className="p-3">
                                <div className="font-bold">{f.name}</div>
                                <div className="text-xs text-zinc-500 font-mono">{f.code}</div>
                            </td>
                            <td className="p-3">{f.member_count}</td>
                            <td className="p-3">
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold brut-border ${STATUS_COLORS[f.status]}`}>{f.status}</span>
                            </td>
                            <td className="p-3" title={f.last_active || ""}>{daysAgo(f.last_active)}</td>
                            <td className="p-3">{f.active_days_7d}/7</td>
                            <td className="p-3">{f.actions_7d}</td>
                            <td className="p-3">{f.actions_30d}</td>
                            <td className="p-3">{f.most_used_feature || "—"}</td>
                            <td className="p-3">{f.completed_chores_7d}</td>
                            <td className="p-3">{f.avg_rating ? `${f.avg_rating}★` : "—"}</td>
                            <td className="p-3">
                                <button
                                    data-testid={`delete-family-${f.code}`}
                                    onClick={() => onDelete(f.household_id, f.name)}
                                    className="w-8 h-8 brut-border rounded-lg bg-white flex items-center justify-center hover:bg-[#F4B7B2]"
                                    title={`Delete ${f.name}`}
                                    aria-label="delete household"
                                >
                                    <Trash className="w-4 h-4 text-[#E86A5C]" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function FeedbackTab({ feedback, filters, setFilters, setFbStatus }) {
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <select
                    data-testid="fb-filter-status"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="brut-input !py-1.5 !text-sm !w-auto"
                >
                    <option value="">All statuses</option>
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="planned">Planned</option>
                    <option value="fixed">Fixed</option>
                    <option value="ignored">Ignored</option>
                </select>
                <label className="flex items-center gap-1.5 text-sm font-bold">
                    <input
                        type="checkbox"
                        data-testid="fb-filter-bug"
                        checked={filters.has_bug}
                        onChange={(e) => setFilters({ ...filters, has_bug: e.target.checked })}
                    />{" "}
                    Bug
                </label>
                <label className="flex items-center gap-1.5 text-sm font-bold">
                    <input
                        type="checkbox"
                        data-testid="fb-filter-feature"
                        checked={filters.has_feature}
                        onChange={(e) => setFilters({ ...filters, has_feature: e.target.checked })}
                    />{" "}
                    Feature
                </label>
            </div>

            {!feedback?.length ? (
                <div className="brut-card p-6 text-center text-sm text-zinc-500">No feedback matches these filters.</div>
            ) : (
                <div className="space-y-3" data-testid="feedback-list">
                    {feedback.map((f) => (
                        <FeedbackCard key={f.id} f={f} setFbStatus={setFbStatus} />
                    ))}
                </div>
            )}
        </div>
    );
}

function FeedbackCard({ f, setFbStatus }) {
    return (
        <div className="brut-card p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold">{f.user_name || f.user_email}</span>
                        <span className="text-xs text-zinc-500">· {f.user_email}</span>
                        {f.household_name && (
                            <span className="text-xs brut-border px-1.5 py-0.5 rounded bg-[#FBF6EB]">{f.household_name}</span>
                        )}
                        <span className={`ml-auto px-2.5 py-0.5 rounded-full text-xs font-bold brut-border ${FEEDBACK_STATUS_COLORS[f.status || "new"]}`}>
                            {f.status || "new"}
                        </span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                        {fmtDate(f.created_at)} {f.rating ? `· ${"★".repeat(f.rating)}` : ""}
                    </div>
                </div>
            </div>
            {f.bug && (
                <div className="mt-3 text-sm">
                    <div className="text-xs uppercase tracking-widest font-bold text-[#E86A5C]">Bug</div>
                    <div className="whitespace-pre-wrap">{f.bug}</div>
                </div>
            )}
            {f.feature && (
                <div className="mt-3 text-sm">
                    <div className="text-xs uppercase tracking-widest font-bold text-[#2F6B43]">Feature request</div>
                    <div className="whitespace-pre-wrap">{f.feature}</div>
                </div>
            )}
            {f.notes && (
                <div className="mt-3 text-sm">
                    <div className="text-xs uppercase tracking-widest font-bold text-[#255636]">Notes</div>
                    <div className="whitespace-pre-wrap">{f.notes}</div>
                </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {(["new", "reviewed", "planned", "fixed", "ignored"]).map((s) => (
                    <button
                        key={s}
                        data-testid={`fb-set-${s}-${f.id}`}
                        onClick={() => setFbStatus(f.id, s)}
                        disabled={f.status === s}
                        className={`text-xs font-bold px-2.5 py-1 brut-border rounded-full transition-colors ${f.status === s ? "bg-[#0D2A1B] text-[#FBF6EB] opacity-70 cursor-default" : "bg-white hover:bg-[#EFEAD8]"
                            }`}
                    >
                        {s}
                    </button>
                ))}
            </div>
        </div>
    );
}


const PLAN_OPTIONS = ["free", "plus", "premium"];
const STATUS_OPTIONS = ["trial", "active", "manual_plus", "manual_premium", "past_due", "canceled"];

function planBadge(plan) {
    const p = plan || "free";
    return (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold border ${PLAN_COLORS[p] || PLAN_COLORS.free}`}>
            {p === "free" ? "Free" : p === "plus" ? "Milo Plus" : "Milo Premium"}
        </span>
    );
}

function statusBadge(status) {
    const s = status || "trial";
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${STATUS_PILL_COLORS[s] || STATUS_PILL_COLORS.trial}`}>
            {s.replace(/_/g, " ")}
        </span>
    );
}

function BillingTab({ users, onUpdate }) {
    const [query, setQuery] = useState("");
    const [filterPlan, setFilterPlan] = useState("");

    const sorted = (users || [])
        .slice()
        .sort((a, b) => {
            const aT = a.last_activity_at || a.created_at || "";
            const bT = b.last_activity_at || b.created_at || "";
            return bT.localeCompare(aT);
        });
    const q = query.trim().toLowerCase();
    let filtered = q
        ? sorted.filter((u) => (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q))
        : sorted;
    if (filterPlan) filtered = filtered.filter((u) => (u.plan || "free") === filterPlan);

    const counts = { free: 0, plus: 0, premium: 0 };
    let paying = 0;
    (users || []).forEach((u) => {
        const p = u.plan || "free";
        if (counts[p] !== undefined) counts[p] += 1;
        if (p !== "free") paying += 1;
    });
    const total = users?.length || 0;

    return (
        <div className="space-y-5" data-testid="billing-tab">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KpiCard label="Total users" value={total} />
                <KpiCard label="Paying" value={paying} hint={`${counts.plus} Plus · ${counts.premium} Premium`} />
                <KpiCard label="Milo Plus" value={counts.plus} />
                <KpiCard label="Milo Premium" value={counts.premium} />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <input
                        data-testid="billing-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search email or name…"
                        className="brut-input !py-1.5 !text-sm w-64"
                    />
                    <select
                        data-testid="billing-filter-plan"
                        value={filterPlan}
                        onChange={(e) => setFilterPlan(e.target.value)}
                        className="brut-input !py-1.5 !text-sm !w-auto"
                    >
                        <option value="">All plans</option>
                        <option value="free">Free</option>
                        <option value="plus">Milo Plus</option>
                        <option value="premium">Milo Premium</option>
                    </select>
                </div>
                <div className="text-xs text-zinc-500">
                    Showing {filtered.length} of {total}. Sorted by most recent activity.
                </div>
            </div>

            {!filtered.length ? (
                <div className="brut-card p-6 text-center text-sm text-zinc-500">No users match.</div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="billing-users-grid">
                    {filtered.map((u) => (
                        <BillingCard key={u.user_id} u={u} onUpdate={onUpdate} />
                    ))}
                </div>
            )}
        </div>
    );
}

function fmtChargedAt(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return null;
        const days = Math.floor((Date.now() - d.getTime()) / 86400000);
        const dateStr = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
        return `${dateStr} (${days === 0 ? "today" : days === 1 ? "1d ago" : days + "d ago"})`;
    } catch {
        return null;
    }
}

function BillingCard({ u, onUpdate }) {
    const [notesDraft, setNotesDraft] = useState(u.billing_notes || "");
    const [cusDraft, setCusDraft] = useState(u.stripe_customer_id || "");

    // Keep drafts in sync if parent refreshes
    useEffect(() => { setNotesDraft(u.billing_notes || ""); }, [u.billing_notes]);
    useEffect(() => { setCusDraft(u.stripe_customer_id || ""); }, [u.stripe_customer_id]);

    const expiry = (u.manual_access_expires_at || "").slice(0, 10);
    const lastCharged = (u.last_charged_at || "").slice(0, 10);

    const markAsCharged = () => {
        const next = (u.plan || "free") === "free" ? "plus" : (u.plan || "plus");
        const nextStatus = next === "premium" ? "manual_premium" : "manual_plus";
        onUpdate(u.user_id, {
            plan: next,
            subscription_status: nextStatus,
            last_charged_at: new Date().toISOString(),
        });
    };

    const saveNotes = () => {
        if ((notesDraft || "") !== (u.billing_notes || "")) {
            onUpdate(u.user_id, { billing_notes: notesDraft });
        }
    };
    const saveCus = () => {
        if ((cusDraft || "") !== (u.stripe_customer_id || "")) {
            onUpdate(u.user_id, { stripe_customer_id: cusDraft });
        }
    };

    const stripeUrl = u.stripe_customer_id
        ? `https://dashboard.stripe.com/customers/${u.stripe_customer_id}`
        : null;

    return (
        <div className="brut-card p-4 flex flex-col gap-3" data-testid={`billing-card-${u.user_id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-bold truncate">{u.name || "—"}</div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                        {planBadge(u.plan)}
                        {statusBadge(u.subscription_status)}
                    </div>
                </div>
                <button
                    data-testid={`billing-mark-charged-${u.user_id}`}
                    onClick={markAsCharged}
                    className="brut-btn brut-btn-primary text-xs !px-3 !py-1.5 whitespace-nowrap"
                    title="Set plan=plus + status=manual_plus + last_charged_at=today"
                >
                    Mark as charged
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs">
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#255636] mb-0.5">Plan</div>
                    <select
                        data-testid={`billing-plan-${u.user_id}`}
                        value={u.plan || "free"}
                        onChange={(e) => onUpdate(u.user_id, { plan: e.target.value })}
                        className="brut-input !py-1 !text-xs w-full"
                    >
                        {PLAN_OPTIONS.map((p) => (<option key={p} value={p}>{p}</option>))}
                    </select>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#255636] mb-0.5">Status</div>
                    <select
                        data-testid={`billing-status-${u.user_id}`}
                        value={u.subscription_status || "trial"}
                        onChange={(e) => onUpdate(u.user_id, { subscription_status: e.target.value })}
                        className="brut-input !py-1 !text-xs w-full"
                    >
                        {STATUS_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#255636] mb-0.5">Last charged</div>
                    <input
                        type="date"
                        data-testid={`billing-charged-${u.user_id}`}
                        value={lastCharged}
                        onChange={(e) => {
                            const v = e.target.value;
                            onUpdate(u.user_id, { last_charged_at: v ? `${v}T00:00:00Z` : "" });
                        }}
                        className="brut-input !py-1 !text-xs w-full"
                    />
                    {lastCharged && (
                        <div className="text-[10px] text-[#5b6d63] mt-0.5">{fmtChargedAt(u.last_charged_at)}</div>
                    )}
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#255636] mb-0.5">Access expires</div>
                    <input
                        type="date"
                        data-testid={`billing-expiry-${u.user_id}`}
                        value={expiry}
                        onChange={(e) => {
                            const v = e.target.value;
                            onUpdate(u.user_id, { manual_access_expires_at: v ? `${v}T23:59:59Z` : "" });
                        }}
                        className="brut-input !py-1 !text-xs w-full"
                    />
                </div>
                <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#255636] mb-0.5 flex items-center justify-between">
                        <span>Stripe customer</span>
                        {stripeUrl && (
                            <a
                                data-testid={`billing-stripe-link-${u.user_id}`}
                                href={stripeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#2F6B43] hover:underline normal-case tracking-normal font-semibold"
                            >
                                open ↗
                            </a>
                        )}
                    </div>
                    <input
                        data-testid={`billing-cus-${u.user_id}`}
                        value={cusDraft}
                        onChange={(e) => setCusDraft(e.target.value)}
                        onBlur={saveCus}
                        placeholder="cus_…"
                        className="brut-input !py-1 !text-xs w-full font-mono"
                    />
                </div>
            </div>

            <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#255636] mb-0.5">Billing notes</div>
                <textarea
                    data-testid={`billing-notes-${u.user_id}`}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    onBlur={saveNotes}
                    placeholder="e.g. Charged $20 via Stripe invoice INV-001 on 2026-05-24"
                    rows={2}
                    className="brut-input !py-1.5 !text-xs w-full resize-none"
                />
            </div>

            <div className="flex items-center justify-between text-[11px] text-zinc-500 pt-1 border-t border-[#0D2A1B]/8">
                <span>Joined {daysAgo(u.created_at)}</span>
                <span>·</span>
                <span>{u.households_count ?? 0} household{(u.households_count ?? 0) === 1 ? "" : "s"}</span>
                <span>·</span>
                <span>Last active {daysAgo(u.last_activity_at)}</span>
            </div>
        </div>
    );
}
